// Mobile-optimized EstimatePage.js
import React, { useState, useEffect } from 'react';
import { Shield, Sparkles, Clock, Send, Star, Car, Check, ChevronDown, MapPin } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ServicesComponent from './ServicesComponent';
import { calculateVehiclePrice } from '../utils/vehiclePricing';

const EstimatePage = ({ isMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const preSelectedService = location.state?.preSelectedService;
  const preSelectedServiceType = location.state?.serviceType;
  const preSelectedServiceCategory = location.state?.serviceCategory;

  // Service category tabs state
  const [activeServiceTab, setActiveServiceTab] = useState('full');
  
  // Form state
  const [formData, setFormData] = useState({
    vehicleType: 'sedan',
    make: '',
    model: '',
    year: '',
    serviceCategory: 'full',
    serviceType: 'gold',
    wheelProtection: false,
    glassProtection: false
  });
  
  // Calculated price state
  const [priceDetails, setPriceDetails] = useState({
    basePrice: 0,
    adjustedPrice: 0,
    vehicleSize: '',
    sizeDescription: '',
    additionalOptions: 0
  });
  
  // Success state
  const [isCalculated, setIsCalculated] = useState(false);
  
  // Services display state
  const [showServicesExpanded, setShowServicesExpanded] = useState(false);

  // Accordion state for mobile
  const [activeAccordion, setActiveAccordion] = useState('vehicle');
  
  // Vehicle makes for the dropdown
  const vehicleMakes = [
    "Select Make", "Acura", "Alfa Romeo", "Aston Martin", "Audi", "Bentley",
    "BMW", "Buick", "Cadillac", "Chevrolet", "Chrysler", "Dodge", "Ferrari",
    "Fiat", "Ford", "Genesis", "GMC", "Honda", "Hyundai", "Infiniti", "Jaguar",
    "Jeep", "Kia", "Lamborghini", "Land Rover", "Lexus", "Lincoln", "Maserati",
    "Mazda", "McLaren", "Mercedes-Benz", "Mini", "Mitsubishi", "Nissan", "Porsche",
    "Ram", "Rolls-Royce", "Subaru", "Tesla", "Toyota", "Volkswagen", "Volvo"
  ];
  
  // Base price estimates based on service type
  const baseServicePrices = {
    full: {
      silver: 195,
      gold: 275,
      diamond: 399
    },
    exterior: {
      classic_exterior: 168,
      wax_polish: 289,
      cut_polish: 439
    },
    interior: {
      classic_interior: 186,
      comfort_interior: 239,
      deep_clean: 289
    },
    ceramic: {
      standard: 599,
      premium: 899,
      elite: 1499
    }
  };
  
  // Additional options pricing
  const additionalOptionPrices = {
    wheelProtection: 99,
    glassProtection: 149
  };
  
  // Toggle accordion for mobile view
  const toggleAccordion = (section) => {
    if (activeAccordion === section) {
      setActiveAccordion(null);
    } else {
      setActiveAccordion(section);
    }
  };
  
  // Add ceramic coating handler - to be triggered from the pre-selected props
  useEffect(() => {
    if (preSelectedService === 'ceramic') {
      // Set serviceCategory directly to 'ceramic'
      setActiveServiceTab('ceramic');
      
      setFormData(prev => ({
        ...prev,
        serviceCategory: 'ceramic',
        serviceType: preSelectedServiceType || 'premium'
      }));
    }
  }, [preSelectedService, preSelectedServiceType, preSelectedServiceCategory]);
  
  // Handle service selection from the ServicesComponent
  const handleServiceSelection = (selection) => {
    if (selection.type === 'category') {
      // User selected a service category (tab change)
      setFormData({
        ...formData,
        serviceCategory: selection.value
      });
      setActiveServiceTab(selection.value);
    } else if (selection.type === 'service') {
      // User selected a specific service package
      setFormData({
        ...formData,
        serviceCategory: selection.category,
        serviceType: selection.service
      });
      
      // Optional: scroll back to the form
      const formElement = document.getElementById('estimate-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth' });
      }
      
      // Reset calculated flag
      setIsCalculated(false);
      
    } else if (selection.type === 'ceramic') {
      // User selected a ceramic coating package directly
      setFormData({
        ...formData,
        serviceCategory: 'ceramic',
        serviceType: selection.service
      });
      
      const formElement = document.getElementById('estimate-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth' });
      }
      
      // Reset calculated flag
      setIsCalculated(false);
    }
  };
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle service category change
    if (name === 'serviceCategory') {
      setActiveServiceTab(value);
      
      // Set default service type based on category
      let defaultServiceType = 'gold';
      if (value === 'exterior') defaultServiceType = 'wax_polish';
      if (value === 'interior') defaultServiceType = 'comfort_interior';
      if (value === 'ceramic') defaultServiceType = 'premium';
      
      setFormData({
        ...formData,
        serviceCategory: value,
        serviceType: defaultServiceType
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Reset calculated flag when service options change
    if (['vehicleType', 'make', 'model', 'serviceCategory', 'serviceType'].includes(name)) {
      setIsCalculated(false);
    }
  };
  
  // Handle checkbox changes for ceramic options
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
    
    // Reset calculated flag
    setIsCalculated(false);
  };
  
  // Handle service tab change (from service overview panel)
  const handleServiceTabChange = (tab) => {
    setActiveServiceTab(tab);
    
    // Set default service type based on category
    let defaultServiceType = 'gold';
    if (tab === 'exterior') defaultServiceType = 'wax_polish';
    if (tab === 'interior') defaultServiceType = 'comfort_interior';
    if (tab === 'ceramic') defaultServiceType = 'premium';
    
    // Update form data
    setFormData({
      ...formData,
      serviceCategory: tab,
      serviceType: defaultServiceType
    });
    
    // Reset calculated flag
    setIsCalculated(false);
  };
  
  // Get service name for display
  const getServiceTypeName = () => {
    switch(formData.serviceType) {
      case 'silver': return 'Silver Full Detail';
      case 'gold': return 'Gold Full Detail';
      case 'diamond': return 'Diamond Full Detail';
      case 'classic_exterior': return 'Classic Exterior';
      case 'wax_polish': return 'Wax & Polish';
      case 'cut_polish': return 'Cut & Polish';
      case 'classic_interior': return 'Classic Interior';
      case 'comfort_interior': return 'Comfort Interior';
      case 'deep_clean': return 'Deep Clean';
      case 'standard': return 'Standard Ceramic Coating (1-Year)';
      case 'premium': return 'Premium Ceramic Coating (3-Year)';
      case 'elite': return 'Elite Ceramic Coating (5-Year)';
      default: return '';
    }
  };

  // Get service description
  const getServiceDescription = () => {
    // Based on service type, return appropriate description
    if (formData.serviceCategory === 'full') {
      if (formData.serviceType === 'silver') return 'Our Refresh Package. Best for maintenance or quick refresh.';
      if (formData.serviceType === 'gold') return 'Our Standard Package. A very thorough inside-out detail.';
      if (formData.serviceType === 'diamond') return 'Ultimate Detail Experience. Includes Full Polish & Shampoo/Extraction.';
    } else if (formData.serviceCategory === 'exterior') {
      if (formData.serviceType === 'classic_exterior') return 'Our Classic Package. Best for maintenance or quick refresh.';
      if (formData.serviceType === 'wax_polish') return 'This Package will remove light swirls and scratches.';
      if (formData.serviceType === 'cut_polish') return 'This Package will remove deep swirls and scratches.';
    } else if (formData.serviceCategory === 'interior') {
      if (formData.serviceType === 'classic_interior') return 'Good for maintaining your vehicle interior.';
      if (formData.serviceType === 'comfort_interior') return 'Enhanced interior cleaning with extra attention to detail.';
      if (formData.serviceType === 'deep_clean') return 'Includes seat & carpet shampoo and conditioner.';
    } else if (formData.serviceCategory === 'ceramic') {
      if (formData.serviceType === 'standard') return 'Our entry-level ceramic coating with 1-year durability.';
      if (formData.serviceType === 'premium') return 'Our most popular ceramic coating with 3-year durability.';
      if (formData.serviceType === 'elite') return 'Our flagship ceramic coating with 5-year durability and enhanced gloss.';
    }
    return 'Professional auto detailing service.';
  };

  // Get service features based on service type
  const getServiceFeatures = () => {
    // Define features for each service type
    const featuresByService = {
      full: {
        silver: {
          'INTERIOR': [
            'Interior Vacuum',
            'Wipe all surfaces',
            'Stains (Spot Treatment)',
            'Windows & Mirrors',
            'Door Jambs',
            'Floor Mats',
            'Detail Trunk'
          ],
          'EXTERIOR': [
            'Quick Hand Wash',
            'Clean Rims & Tires',
            'Wax Protection (3 Months)'
          ]
        },
        gold: {
          'INTERIOR': [
            'Double Vacuum Interior',
            'Wipe all surfaces',
            'Stains (Spot Treatment)',
            'Windows & Mirrors',
            'Clean & Protect Plastic',
            'Detail Floor Mats and Shine',
            'Detail Trunk & Door Jambs'
          ],
          'EXTERIOR': [
            'Spot Polish',
            'Professional Hand Wash',
            'Clay Bar Exterior',
            'Detail Rims & Tires',
            'Wheel Wells',
            'Wax Protection (3 Months)'
          ]
        },
        diamond: {
          'INTERIOR': [
            'Shampoo Seats & Carpet',
            'Double Vacuum Interior',
            'Wipe all Surfaces',
            'Stain (Spot Treatment)',
            'Clean & Protect Plastic',
            'Windows & Mirrors',
            'Detail Floor Mats and Shine',
            'Detail Trunk'
          ],
          'EXTERIOR': [
            'Premium 1-Step Polish',
            'Professional Hand Wash',
            'Clay Bar Exterior',
            'Wash Wheel Wells',
            'Dress Trims/Tires',
            'Clean Door Jams',
            'Wax Protection (3 Months)'
          ]
        }
      },
      exterior: {
        classic_exterior: [
          'Basic Spot Polish',
          'Professional Hand Wash',
          'Clay Bar Exterior',
          'Wheel Wells',
          'Detail Rim & Tires',
          'Window & Mirrors',
          'Wax Protection (3 Months)'
        ],
        wax_polish: [
          '1-Step Paint Correction Polish',
          'Professional Hand Wash',
          'Clay Bar Exterior',
          'Wheel Wells',
          'Detail Rim & Tires',
          'Window & Mirrors',
          'Wax Protection (3 Months)'
        ],
        cut_polish: [
          '2-Step Paint Correction Polish',
          'Professional Hand Wash',
          'Clay Bar Exterior',
          'Wheel Wells',
          'Detail Rim & Tires',
          'Window & Mirrors',
          'Wax Protection (3 Months)'
        ]
      },
      interior: {
        classic_interior: [
          'Complete Interior Vacuum',
          'Wipe All Surface',
          'Stains (Spots Treatment)',
          'Windows & Mirrors',
          'Detail Floor Mats and Shine',
          'Detail Trunk'
        ],
        comfort_interior: [
          'Double Interior Vacuum',
          'Complete Surface Cleaning',
          'Stain Treatment & Spot Cleaning',
          'Clean & Protect Plastic Surfaces',
          'Windows & Mirrors Polishing',
          'Deep Clean Door Panels & Jambs',
          'Detail Floor Mats and Apply Protectant',
          'Complete Trunk Detailing'
        ],
        deep_clean: [
          'Deep Cleaning Treatment',
          'Shampoo All Carpets & Seats',
          'Double Vacuum Interior',
          'Stain Extraction',
          'Clean & Protect All Plastic',
          'Windows & Mirrors',
          'Detail Floor Mats and Shine',
          'Detail Trunk'
        ]
      },
      ceramic: {
        standard: [
          'Paint Correction (1-Step)',
          'SiO2-Based Ceramic Coating',
          '1-Year Protection',
          'Water & Dirt Repellent',
          'Enhanced Gloss Finish',
          'UV Protection',
          'Chemical Resistance'
        ],
        premium: [
          'Paint Correction (2-Step)',
          'Advanced SiO2/TiO2 Ceramic Coating',
          '3-Year Protection',
          'Superior Water & Dirt Repellent',
          'High Gloss Finish',
          'Enhanced UV Protection',
          'Chemical & Scratch Resistance'
        ],
        elite: [
          'Paint Correction (3-Step)',
          'Professional-Grade Ceramic Coating',
          '5-Year Protection',
          'Ultimate Water & Dirt Repellent',
          'Mirror Gloss Finish',
          'Maximum UV Protection',
          'Superior Chemical & Scratch Resistance',
          'Heat Resistance'
        ]
      }
    };
    
    return featuresByService[formData.serviceCategory]?.[formData.serviceType] || [];
  };
  
  // Render additional options for ceramic coating
  const renderAdditionalOptions = () => {
    if (formData.serviceCategory === 'ceramic') {
      return (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
          <h4 className="text-sm font-semibold text-blue-800 mb-2">Additional Options</h4>
          
          <div className="space-y-2">
            <label className="flex items-center">
              <input 
                type="checkbox" 
                name="wheelProtection"
                className="mr-2"
                checked={formData.wheelProtection || false}
                onChange={handleCheckboxChange}
              />
              <span className="text-xs text-gray-700">Wheel Protection (+$99)</span>
            </label>
            <label className="flex items-center">
              <input 
                type="checkbox" 
                name="glassProtection"
                className="mr-2"
                checked={formData.glassProtection || false}
                onChange={handleCheckboxChange}
              />
              <span className="text-xs text-gray-700">Glass Protection (+$149)</span>
            </label>
          </div>
        </div>
      );
    }
    return null;
  };
  
  // Calculate price
  const calculatePrice = () => {
    // Get base price for the selected service
    const basePrice = baseServicePrices[formData.serviceCategory][formData.serviceType] || 0;
    
    // Create vehicle object for calculation
    const vehicle = {
      make: formData.make,
      model: formData.model,
      type: formData.vehicleType
    };
    
    // Calculate vehicle-adjusted price using the updated calculateVehiclePrice function
    const { price: adjustedPrice, vehicleSize, sizeDescription, multiplier } = calculateVehiclePrice(basePrice, vehicle);
    
    // Calculate additional options cost
    let additionalOptions = 0;
    
    // Add additional options price if applicable (only for ceramic coating)
    if (formData.serviceCategory === 'ceramic') {
      if (formData.wheelProtection) {
        additionalOptions += additionalOptionPrices.wheelProtection;
      }
      
      if (formData.glassProtection) {
        additionalOptions += additionalOptionPrices.glassProtection;
      }
    }
    
    // Set price details
    setPriceDetails({
      basePrice,
      adjustedPrice,
      vehicleSize,
      sizeDescription,
      additionalOptions,
      multiplier: vehicleSize === 'STANDARD' || vehicleSize === 'COMPACT' ? 1 : multiplier // Ensure multiplier is 1 for standard/compact
    });
    
    // Set calculated flag
    setIsCalculated(true);
    
    // Scroll to the results after calculation
    setTimeout(() => {
      const resultsElement = document.getElementById('price-results');
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  
  // Get final price
  const getFinalPrice = () => {
    return priceDetails.adjustedPrice + priceDetails.additionalOptions;
  };

  // Handle book service button
  const handleBookService = () => {
    // Create a complete service object with all necessary details
    const serviceToBook = {
      id: formData.serviceType,
      title: getServiceTypeName(),
      price: priceDetails.basePrice,
      originalPrice: priceDetails.basePrice, // Always store original price
      // Only set adjustedPrice if it's actually different from the base price
      ...(priceDetails.vehicleSize !== 'STANDARD' && priceDetails.vehicleSize !== 'COMPACT' 
          ? { adjustedPrice: priceDetails.adjustedPrice } 
          : {}),
      category: formData.serviceCategory,
      description: getServiceDescription(),
      features: getServiceFeatures(),
      // Include vehicle information if provided
      vehicleDetails: formData.make || formData.model ? {
        vehicle: {
          make: formData.make,
          model: formData.model,
          year: formData.year,
          type: formData.vehicleType
        }
      } : null,
      // Include pricing details for vehicle size adjustments
      pricingDetails: {
        basePrice: priceDetails.basePrice,
        price: priceDetails.adjustedPrice,
        vehicleSize: priceDetails.vehicleSize,
        multiplier: priceDetails.vehicleSize === 'STANDARD' || priceDetails.vehicleSize === 'COMPACT' 
                 ? 1 
                 : priceDetails.multiplier,
        sizeDescription: priceDetails.sizeDescription,
        additionalOptions: priceDetails.additionalOptions || 0,
        noMarkupForStandardVehicles: true // Flag for booking system
      }
    };
    
    // Add ceramic coating options if selected (only for ceramic coating category)
    if (formData.serviceCategory === 'ceramic') {
      serviceToBook.ceramicOptions = {
        package: formData.serviceType, // Standard, Premium, or Elite
        wheelProtection: formData.wheelProtection,
        glassProtection: formData.glassProtection
      };
    }
    
    // Calculate and include the final total price
    serviceToBook.totalPrice = getFinalPrice();
    
    // Store the complete service in localStorage
    localStorage.setItem('selectedService', JSON.stringify(serviceToBook));
    
    // Navigate to booking page with this data - use estimatedService as the key
    navigate('/book', { 
      state: { 
        estimatedService: serviceToBook
      }
    });
  };
  
  // Synchronize the service component's tab state with our form
  useEffect(() => {
    if (showServicesExpanded) {
      // Scroll to services section with animation
      const servicesSection = document.getElementById('services-section');
      if (servicesSection) {
        servicesSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [showServicesExpanded]);
  
  // Vehicle type labels for display
  const vehicleTypeLabels = {
    sedan: 'Sedan / Coupe',
    suv: 'SUV / Crossover',
    truck: 'Truck / Van',
    luxury: 'Luxury / Exotic'
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Hero section - optimized for mobile */}
      <section className="bg-gradient-to-r from-blue-600 to-cyan-500 py-8 px-4 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-white" 
               style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>

        {/* Location badge */}
        <div className="absolute top-2 left-0 right-0 flex justify-center">
          <div className="inline-flex items-center py-1 px-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
            <MapPin className="h-3 w-3 text-cyan-400 mr-1" />
            <span className="text-white/90 text-xs font-medium">Serving Throughout Chicagoland</span>
          </div>
        </div>
        
        {/* Content */}
        <div className="max-w-4xl mx-auto relative mt-6">
          <div className="text-center text-white">
            <h1 className="text-2xl md:text-5xl font-bold mb-3">
              Price Estimator
            </h1>
            <div className="w-16 h-1 bg-white/30 mx-auto mb-4"></div>
            <p className="text-white/90 text-sm max-w-xl mx-auto">
              Calculate your personalized detailing service price based on your vehicle and preferences.
            </p>
          </div>
        </div>
      </section>
      
      {/* Benefits section - horizontal scrollable for mobile */}
      <section className="bg-white py-3 border-b border-gray-200 shadow-sm overflow-x-auto">
        <div className="flex justify-start md:justify-between gap-4 px-4 min-w-max">
          <div className="flex items-center gap-2 whitespace-nowrap">
            <Shield className="h-4 w-4 text-cyan-500" />
            <span className="text-gray-700 text-xs">Premium Protection</span>
          </div>
          <div className="flex items-center gap-2 whitespace-nowrap">
            <Sparkles className="h-4 w-4 text-yellow-500" />
            <span className="text-gray-700 text-xs">Professional Results</span>
          </div>
          <div className="flex items-center gap-2 whitespace-nowrap">
            <Clock className="h-4 w-4 text-cyan-500" />
            <span className="text-gray-700 text-xs">Fast Service</span>
          </div>
          <div className="flex items-center gap-2 whitespace-nowrap">
            <Star className="h-4 w-4 text-blue-500" />
            <span className="text-gray-700 text-xs">5-Star Rated</span>
          </div>
        </div>
      </section>
      
      {/* Main content */}
      <section className="py-6 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Calculator Card - Optimized for mobile */}
          <div id="estimate-form" className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
            <div className="p-5">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Calculate Your Price</h2>
              
              {/* Service Category Tabs - Scrollable for mobile */}
              <div className="mb-5 overflow-x-auto pb-1 -mx-1">
                <div className="flex min-w-max gap-1 px-1">
                  <button
                    onClick={() => handleServiceTabChange('full')}
                    className={`py-2 px-3 rounded-lg text-center text-xs font-medium transition-colors whitespace-nowrap ${
                      activeServiceTab === 'full' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Full Detail
                  </button>
                  <button
                    onClick={() => handleServiceTabChange('exterior')}
                    className={`py-2 px-3 rounded-lg text-center text-xs font-medium transition-colors whitespace-nowrap ${
                      activeServiceTab === 'exterior' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Exterior Only
                  </button>
                  <button
                    onClick={() => handleServiceTabChange('interior')}
                    className={`py-2 px-3 rounded-lg text-center text-xs font-medium transition-colors whitespace-nowrap ${
                      activeServiceTab === 'interior' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Interior Only
                  </button>
                  <button
                    onClick={() => handleServiceTabChange('ceramic')}
                    className={`py-2 px-3 rounded-lg text-center text-xs font-medium transition-colors whitespace-nowrap ${
                      activeServiceTab === 'ceramic' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Ceramic Coating
                  </button>
                </div>
              </div>
              
              {/* Mobile-optimized accordion sections */}
              <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                {/* Vehicle Type Section */}
                <div className="border-b border-gray-200 last:border-b-0">
                  <button
                    onClick={() => toggleAccordion('vehicle')}
                    className="flex justify-between items-center w-full p-3 text-left focus:outline-none"
                  >
                    <h3 className="text-sm font-medium text-gray-700">Vehicle Information</h3>
                    <ChevronDown 
                      className={`h-4 w-4 text-gray-500 transition-transform ${
                        activeAccordion === 'vehicle' ? 'transform rotate-180' : ''
                      }`} 
                    />
                  </button>
                  
                  {activeAccordion === 'vehicle' && (
                    <div className="p-3 pt-0 bg-gray-50">
                      <div className="space-y-3">
                        {/* Vehicle Type */}
                        <div>
                          <label htmlFor="vehicleType" className="block text-xs font-medium text-gray-700 mb-1">
                            Vehicle Type
                          </label>
                          <select
                            id="vehicleType"
                            name="vehicleType"
                            value={formData.vehicleType}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white"
                          >
                            <option value="sedan">Sedan / Coupe</option>
                            <option value="suv">SUV / Crossover</option>
                            <option value="truck">Truck / Van</option>
                            <option value="luxury">Luxury / Exotic</option>
                          </select>
                        </div>
                        
                        {/* Vehicle Make */}
                        <div>
                          <label htmlFor="make" className="block text-xs font-medium text-gray-700 mb-1">
                            Make (Optional)
                          </label>
                          <select
                            id="make"
                            name="make"
                            value={formData.make}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white"
                          >
                            {vehicleMakes.map((make, index) => (
                              <option key={index} value={index === 0 ? '' : make}>
                                {make}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Vehicle Model */}
                        <div>
                          <label htmlFor="model" className="block text-xs font-medium text-gray-700 mb-1">
                            Model (Optional)
                          </label>
                          <input
                            type="text"
                            id="model"
                            name="model"
                            value={formData.model}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                            placeholder="e.g. Civic, Camry, F-150"
                          />
                        </div>
                        
                        {/* Year */}
                        <div>
                          <label htmlFor="year" className="block text-xs font-medium text-gray-700 mb-1">
                            Year (Optional)
                          </label>
                          <input
                            type="text"
                            id="year"
                            name="year"
                            value={formData.year}
                            onChange={handleChange}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                            placeholder="e.g. 2022"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Service Type Section */}
                <div className="border-b border-gray-200 last:border-b-0">
                  <button
                    onClick={() => toggleAccordion('service')}
                    className="flex justify-between items-center w-full p-3 text-left focus:outline-none"
                  >
                    <h3 className="text-sm font-medium text-gray-700">Service Package</h3>
                    <ChevronDown 
                      className={`h-4 w-4 text-gray-500 transition-transform ${
                        activeAccordion === 'service' ? 'transform rotate-180' : ''
                      }`} 
                    />
                  </button>
                  
                  {activeAccordion === 'service' && (
                    <div className="p-3 pt-0 bg-gray-50">
                      <div className="space-y-2">
                        {formData.serviceCategory === 'full' && (
                          <>
                            <div
                              onClick={() => handleChange({ target: { name: 'serviceType', value: 'silver' } })}
                              className={`cursor-pointer border rounded-lg p-3 transition-all ${
                                formData.serviceType === 'silver' 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">Silver</div>
                                  <div className="text-gray-600 text-xs">Basic Detail</div>
                                </div>
                                <div className="text-blue-600 font-medium">$195</div>
                              </div>
                            </div>
                            <div
                              onClick={() => handleChange({ target: { name: 'serviceType', value: 'gold' } })}
                              className={`cursor-pointer border rounded-lg p-3 transition-all ${
                                formData.serviceType === 'gold' 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">Gold</div>
                                  <div className="text-gray-600 text-xs">Popular Choice</div>
                                </div>
                                <div className="text-blue-600 font-medium">$275</div>
                              </div>
                            </div>
                            <div
                              onClick={() => handleChange({ target: { name: 'serviceType', value: 'diamond' } })}
                              className={`cursor-pointer border rounded-lg p-3 transition-all ${
                                formData.serviceType === 'diamond' 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">Diamond</div>
                                  <div className="text-gray-600 text-xs">Premium Service</div>
                                </div>
                                <div className="text-blue-600 font-medium">$399</div>
                              </div>
                            </div>
                          </>
                        )}
                        
                        {formData.serviceCategory === 'exterior' && (
                          <>
                            <div
                              onClick={() => handleChange({ target: { name: 'serviceType', value: 'classic_exterior' } })}
                              className={`cursor-pointer border rounded-lg p-3 transition-all ${
                                formData.serviceType === 'classic_exterior' 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">Classic</div>
                                  <div className="text-gray-600 text-xs">Basic Clean</div>
                                </div>
                                <div className="text-blue-600 font-medium">$168</div>
                              </div>
                            </div>
                            <div
                              onClick={() => handleChange({ target: { name: 'serviceType', value: 'wax_polish' } })}
                              className={`cursor-pointer border rounded-lg p-3 transition-all ${
                                formData.serviceType === 'wax_polish' 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">Wax & Polish</div>
                                  <div className="text-gray-600 text-xs">Enhanced Shine</div>
                                </div>
                                <div className="text-blue-600 font-medium">$289</div>
                              </div>
                            </div>
                            <div
                              onClick={() => handleChange({ target: { name: 'serviceType', value: 'cut_polish' } })}
                              className={`cursor-pointer border rounded-lg p-3 transition-all ${
                                formData.serviceType === 'cut_polish' 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">Cut & Polish</div>
                                  <div className="text-gray-600 text-xs">Paint Correction</div>
                                </div>
                                <div className="text-blue-600 font-medium">$439</div>
                              </div>
                            </div>
                          </>
                        )}
                        
                        {formData.serviceCategory === 'interior' && (
                          <>
                            <div
                              onClick={() => handleChange({ target: { name: 'serviceType', value: 'classic_interior' } })}
                              className={`cursor-pointer border rounded-lg p-3 transition-all ${
                                formData.serviceType === 'classic_interior' 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">Classic</div>
                                  <div className="text-gray-600 text-xs">Basic Clean</div>
                                </div>
                                <div className="text-blue-600 font-medium">$186</div>
                              </div>
                            </div>
                            <div
                              onClick={() => handleChange({ target: { name: 'serviceType', value: 'comfort_interior' } })}
                              className={`cursor-pointer border rounded-lg p-3 transition-all ${
                                formData.serviceType === 'comfort_interior' 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">Comfort</div>
                                  <div className="text-gray-600 text-xs">Popular Choice</div>
                                </div>
                                <div className="text-blue-600 font-medium">$239</div>
                              </div>
                            </div>
                            <div
                              onClick={() => handleChange({ target: { name: 'serviceType', value: 'deep_clean' } })}
                              className={`cursor-pointer border rounded-lg p-3 transition-all ${
                                formData.serviceType === 'deep_clean' 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">Deep Clean</div>
                                  <div className="text-gray-600 text-xs">Premium Clean</div>
                                </div>
                                <div className="text-blue-600 font-medium">$289</div>
                              </div>
                            </div>
                          </>
                        )}
                        
                        {formData.serviceCategory === 'ceramic' && (
                          <>
                            <div
                              onClick={() => handleChange({ target: { name: 'serviceType', value: 'standard' } })}
                              className={`cursor-pointer border rounded-lg p-3 transition-all ${
                                formData.serviceType === 'standard' 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">Standard</div>
                                  <div className="text-gray-600 text-xs">1-Year Protection</div>
                                </div>
                                <div className="text-blue-600 font-medium">$599</div>
                              </div>
                            </div>
                            <div
                              onClick={() => handleChange({ target: { name: 'serviceType', value: 'premium' } })}
                              className={`cursor-pointer border rounded-lg p-3 transition-all ${
                                formData.serviceType === 'premium' 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">Premium</div>
                                  <div className="text-gray-600 text-xs">3-Year Protection</div>
                                </div>
                                <div className="text-blue-600 font-medium">$899</div>
                              </div>
                            </div>
                            <div
                              onClick={() => handleChange({ target: { name: 'serviceType', value: 'elite' } })}
                              className={`cursor-pointer border rounded-lg p-3 transition-all ${
                                formData.serviceType === 'elite' 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">Elite</div>
                                  <div className="text-gray-600 text-xs">5-Year Protection</div>
                                </div>
                                <div className="text-blue-600 font-medium">$1499</div>
                              </div>
                            </div>
                            
                            {/* Ceramic coating additional options */}
                            {renderAdditionalOptions()}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* View Service Details Button */}
              <div className="mb-4">
                <button
                  type="button"
                  onClick={() => setShowServicesExpanded(!showServicesExpanded)}
                  className="text-blue-500 text-sm font-medium flex items-center hover:text-blue-600"
                >
                  {showServicesExpanded ? 'Hide service details' : 'Browse all service packages'}
                  <ChevronDown 
                    className={`w-4 h-4 ml-1 transition-transform duration-300 ${showServicesExpanded ? 'rotate-180' : ''}`}
                  />
                </button>
              </div>
              
              {/* Calculate button */}
              <div className="mt-6">
                <button
                  onClick={calculatePrice}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-400 text-white py-3 px-6 rounded-lg font-medium hover:from-blue-600 hover:to-cyan-500 transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  <Sparkles className="h-5 w-5" />
                  <span>Calculate Price</span>
                </button>
              </div>
            </div>
          </div>
          
          {/* Price Results - Optimized for mobile */}
          {isCalculated && (
            <div id="price-results" className="bg-white rounded-xl shadow-lg p-5 mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Your Price Estimate</h2>
              
              <div className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg p-4 mb-5">
                <div className="text-sm font-medium mb-1">Total Price:</div>
                <div className="text-3xl font-bold">${getFinalPrice()}</div>
                <div className="text-xs text-white/80 mt-1">
                  for {getServiceTypeName()} {formData.make ? `(${formData.make})` : ''}
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <h3 className="font-medium text-gray-800 mb-2 text-sm">Service Details</h3>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-gray-600 text-xs">Service Package:</div>
                    <div className="font-medium text-right text-xs">{getServiceTypeName()}</div>
                    
                    <div className="text-gray-600 text-xs">Base Price:</div>
                    <div className="font-medium text-right text-xs">${priceDetails.basePrice}</div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <h3 className="font-medium text-gray-800 mb-2 text-sm">Vehicle Details</h3>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="text-gray-600">Vehicle Type:</div>
                    <div className="font-medium text-right">
                      {vehicleTypeLabels[formData.vehicleType]}
                    </div>
                    
                    {formData.make && (
                      <>
                        <div className="text-gray-600">Make:</div>
                        <div className="font-medium text-right">{formData.make}</div>
                      </>
                    )}
                    
                    {formData.model && (
                      <>
                        <div className="text-gray-600">Model:</div>
                        <div className="font-medium text-right">{formData.model}</div>
                      </>
                    )}
                    
                    <div className="text-gray-600">Size Category:</div>
                    <div className="font-medium text-right">
                      {priceDetails.vehicleSize}
                      {(priceDetails.vehicleSize === 'STANDARD' || priceDetails.vehicleSize === 'COMPACT') && 
                        <span className="text-green-600 text-xs ml-1">(No markup)</span>
                      }
                    </div>
                    
                    {/* Only show multiplier if there is actually a markup */}
                    {priceDetails.vehicleSize !== 'STANDARD' && priceDetails.vehicleSize !== 'COMPACT' && priceDetails.multiplier !== 1 && (
                      <>
                        <div className="text-gray-600">Multiplier:</div>
                        <div className="font-medium text-right">{priceDetails.multiplier}x</div>
                      </>
                    )}
                    
                    {/* Change label based on whether there's a markup */}
                    <div className="text-gray-600">
                      {(priceDetails.vehicleSize === 'STANDARD' || priceDetails.vehicleSize === 'COMPACT') 
                        ? 'Final Price:' 
                        : 'Size Adjusted Price:'}
                    </div>
                    <div className="font-medium text-right">${priceDetails.adjustedPrice}</div>
                  </div>
                </div>
                
                {priceDetails.additionalOptions > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h3 className="font-medium text-gray-800 mb-2 text-sm">Add-on Services</h3>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {formData.serviceCategory === 'ceramic' && (
                        <>
                          {formData.wheelProtection && (
                            <>
                              <div className="text-gray-600">Wheel Protection:</div>
                              <div className="font-medium text-right">$99</div>
                            </>
                          )}
                          
                          {formData.glassProtection && (
                            <>
                              <div className="text-gray-600">Glass Protection:</div>
                              <div className="font-medium text-right">$149</div>
                            </>
                          )}
                        </>
                      )}
                      
                      <div className="text-gray-600 pt-2 border-t border-gray-200">
                        Add-on Subtotal:
                      </div>
                      <div className="font-medium text-right pt-2 border-t border-gray-200">
                        ${priceDetails.additionalOptions}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Book Now Button after calculation */}
              <div className="mt-5">
                <button 
                  onClick={handleBookService}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-all duration-300 shadow-md"
                >
                  <Send className="h-5 w-5" />
                  <span>Book This Service</span>
                </button>
              </div>
            </div>
          )}
          
          {!isCalculated && (
            <div className="bg-white rounded-xl shadow-lg p-5 mb-6 text-center">
              <Car className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                Your Price Estimate
              </h3>
              <p className="text-gray-500 text-sm">
                Fill out the form above and click "Calculate Price" to see your personalized service estimate.
              </p>
            </div>
          )}
          
          {/* Why Choose Us - Compact mobile version */}
          <div className="bg-white rounded-xl shadow-lg p-5">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Why Choose Our Services?</h3>
            
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Shield className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 text-sm">Premium Products</h4>
                  <p className="text-gray-600 text-xs">Professional-grade, eco-friendly products for superior results.</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 text-sm">Professional Results</h4>
                  <p className="text-gray-600 text-xs">Certified technicians delivering showroom-quality results.</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Clock className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 text-sm">Mobile Service</h4>
                  <p className="text-gray-600 text-xs">We come to your location with all necessary equipment.</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Check className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 text-sm">Satisfaction Guarantee</h4>
                  <p className="text-gray-600 text-xs">100% satisfaction guarantee on all our services.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Services section - expanded view with ability to select services */}
      {showServicesExpanded && (
        <div id="services-section" className="py-6 bg-gray-50 border-t border-gray-200">
          <ServicesComponent 
            activeTab={formData.serviceCategory} 
            onSelectService={handleServiceSelection}
          />
        </div>
      )}
      
      {/* Condensed CTA Section for mobile */}
      <section className="py-8 px-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-3">Ready to Transform Your Vehicle?</h2>
          <div className="flex flex-col gap-3 mt-4">
            <Link to="/book">
              <button className="w-full bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-all shadow-md">
                Book Now
              </button>
            </Link>
            <Link to="/services">
              <button className="w-full bg-transparent border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-all">
                Explore Services
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EstimatePage;